// material-ui
import { Grid } from '@material-ui/core';
import React from 'react';

// api
import { getNetwork, INetwork, transformNetworkWrapper } from '~/src/dash/api/network';
import { IHttpResponse } from '~/src/dash/api/rest';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  parseLocation,
  RouteNetworkList,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';
// views
import { LoadingView } from '~/src/dash/views/loading-view';
import { NetworkSettingsView } from '~/src/dash/views/networks/network-settings-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  currentNetwork?: INetwork;
  cloneReferenceID?: number;
  // error messaging
  snackbarMessage: string;
  snackbarError: boolean;
  // ok to render the network settings view
  loaded: boolean;
}

export const NetworkSettingsCloneView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      currentNetwork: undefined,
      cloneReferenceID: 0,
      snackbarMessage: '',
      snackbarError: false,
      loaded: false,
    };

    componentDidMount() {
      this.getClonedNetworkAndRemoveOldRefs();
    }

    getClonedNetworkAndRemoveOldRefs(): void {
      // get network id from the url and make API call
      const loc = parseLocation(this.props.location.pathname);
      if (loc.network) {
        getNetwork(loc.network, this.props.session.publisher.ID)
          .then((val: IHttpResponse<INetwork>) => {
            const currentNetwork = transformNetworkWrapper(val.parsedBody);
            this.setState({
              cloneReferenceID: currentNetwork.ID,
            });
            // add 'copied' to the name so it is clear this is a clone and delete the network's ID
            currentNetwork.Name = currentNetwork.Name + ' - Copied';
            currentNetwork.ID = 0;
            // remove ID references from associations
            for (let i = 0; i < currentNetwork.NetworkTrackers.length; i++) {
              currentNetwork.NetworkTrackers[i].NetworkID = 0;
              currentNetwork.NetworkTrackers[i].ID = 0;
            }
            for (let i = 0; i < currentNetwork.Channels.length; i++) {
              currentNetwork.Channels[i].NetworkID = 0;
              currentNetwork.Channels[i].ID = 0;
            }
            for (let i = 0; i < currentNetwork.NetworkPublisherMaps.length; i++) {
              currentNetwork.NetworkPublisherMaps[i].NetworkID = 0;
              currentNetwork.NetworkPublisherMaps[i].ID = 0;
            }
            for (let i = 0; i < currentNetwork.NetworkInVideoUnits.length; i++) {
              currentNetwork.NetworkInVideoUnits[i].NetworkID = 0;
              currentNetwork.NetworkInVideoUnits[i].ID = 0;
            }
            this.setState({
              currentNetwork,
              loaded: true,
            });
          })
          .catch(() => {
            this.setState(
              {
                snackbarMessage:
                  "You don't have access to this resource. Please create a new one or select one from the list.",
                snackbarError: true,
              },
              () => {
                setTimeout(() => {
                  navigate(
                    RouteNetworkList,
                    extractRouteParams({
                      session: this.props.session,
                      user: this.props.user,
                    }),
                  );
                }, 3000);
              },
            );
          });
      }
    }

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render() {
      const { loaded, currentNetwork, snackbarMessage, snackbarError } = this.state;
      if (loaded) {
        return (
          <Grid>
            <NetworkSettingsView
              isClone={true}
              currentNetwork={currentNetwork}
              cloneReferenceID={this.state.cloneReferenceID}
            />
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Grid>
        );
      } else {
        return <LoadingView active={true} text='Loading the network settings' />;
      }
    }
  },
);
